import { createTheme, Theme, ThemeOptions } from '@mui/material/styles'
import { CustomStyles, CTAStyles, SeverityStyles, TooltipStyles, ListSelectionStyles } from '../types'
import { colorPalette } from './color-palette'
import { buttonVariantStyles } from './buttonVariants'

const storePalette = colorPalette.clearly

const {
  wildSand,
  white,
  alto,
  boulder,
  tulipTree,
  thunderBird,
  greenPea,
  black,
  light,
  dark1,
  light2,
  blue,
  light1,
  grey,
  red,
  purple,
  brown,
  green,
  athensGrey,
  cornflowerBlue,
} = storePalette

/**
 * Default theme options
 */
export const themeProps = {
  themeName: 'Clearly',
  name: 'Clearly',
  typography: {
    fontFamily: 'inherit',
    htmlFontSize: 16,
    textTransform: 'none',
    body: {
      fontSize: '1rem',
      lineHeight: 24,
    },
    body2: {
      fontSize: '0.875rem',
    },
    h1: {
      fontSize: '2.5rem',
    },
    h2: {
      fontSize: '2rem',
    },
    h3: {
      fontSize: '1.5rem',
    },
    h4: {
      fontSize: '1.25rem',
    },
    h5: {
      fontSize: '1.25rem',
    },
    h6: {
      fontSize: '1.25rem',
    },
    subtitle1: {
      fontSize: '0.75rem',
    },
    subtitle2: {
      fontSize: '0.625rem',
    },
  },
  palette: {
    type: 'light',
    background: {
      dark: {
        primary: black,
        secondary: blue,
        tertiary: light1.grey,
        quaternary: light.blue,
      },
      light: {
        primary: white,
        secondary: white,
        tertiary: light2.grey,
        quaternary: boulder,
        quinary: light.grey,
        senary: light.red,
      },
    },
    text: {
      dark: {
        primary: black,
        secondary: blue,
        tertiary: light.blue,
        quaternary: blue,
        quinary: grey,
        senary: red,
      },
      light: {
        primary: white,
        secondary: boulder,
        tertiary: alto,
        quaternary: wildSand,
      },
    },
    color: {
      info: light.blue,
      success: greenPea,
      warning: tulipTree,
      error: thunderBird,
      black: black,
      grey: grey,
    },
    action: {
      hover: dark1.blue,
    },
    custom: storePalette,
  },
  spacing: 4,
  button: {
    backgroundColor: dark1.blue,
    '&:hover': {
      backgroundColor: dark1.blue,
    },
  },
  child: {
    backgroundColor: dark1.blue,
  },
  rippleVisible: {
    opacity: 0.5,
  },
  breakpoints: {
    values: {
      xs: 320,
      sm: 600,
      md: 1025,
      lg: 1440,
      xl: 1920,
    },
  },
  shadows: [
    'none',
    '0px 1px 4px 2px rgba(0,0,0,0.05), 0px 2px 1px 0px rgba(0,0,0,0.03)',
    '0px 1px 4px 2px rgba(0,0,0,0.15), 0px 2px 1px 0px rgba(0,0,0,0.13)',
    '0px 1px 2px 2px rgba(0,0,0,0.1), 0px 2px 1px 0px rgba(0,0,0,0.1)',
    'inset 0px 1px 2px 2px rgba(0,0,0,0.1), inset 0px 2px 1px 0px rgba(0,0,0,0.1)',
    'inset 0px 1.47273px 11.7818px rgba(96, 97, 112, 0.32)',
    '0px 0px 2.94545px rgba(40, 41, 61, 0.08), 0px 1.47273px 5.89091px rgba(96, 97, 112, 0.16)',
    '0px 0px 2.94545px rgba(40, 41, 61, 0.04), 0px 5.89091px 11.7818px rgba(96, 97, 112, 0.16)',
    '0px 0px 5.89091px rgba(40, 41, 61, 0.04), 0px 11.7818px 23.5636px rgba(96, 97, 112, 0.16)',
    '0px 5.89091px 11.7818px rgba(40, 41, 61, 0.04), 0px 23.5636px 47.1273px rgba(96, 97, 112, 0.16)',
    '0px 5.89091px 23.5636px rgba(40, 41, 61, 0.04), 0px 47.1273px 70.6909px rgba(96, 97, 112, 0.16)',
    '0px 5.89091px 23.5636px rgba(40, 41, 61, 0.08), 0px 58.9091px 94.2545px rgba(96, 97, 112, 0.24)',
    'inset 0px 1.47273px 11.7818px rgba(96, 97, 112, 0.32)',
    '0px 0px 2.94545px rgba(0, 0, 0, 0.08), 0px 1.47273px 5.89091px rgba(0, 0, 0, 0.16)',
    '0px 0px 2.94545px rgba(0, 0, 0, 0.04), 0px 5.89091px 11.7818px rgba(0, 0, 0, 0.16)',
    '0px 0px 5.89091px rgba(0, 0, 0, 0.04), 0px 11.7818px 23.5636px rgba(0, 0, 0, 0.16)',
    '0px 5.89091px 11.7818px rgba(0, 0, 0, 0.04), 0px 23.5636px 47.1273px rgba(0, 0, 0, 0.16)',
    '0px 5.89091px 23.5636px rgba(0, 0, 0, 0.04), 0px 47.1273px 70.6909px rgba(0, 0, 0, 0.16)',
    '0px 5.89091px 23.5636px rgba(0, 0, 0, 0.08), 0px 58.9091px 94.2545px rgba(0, 0, 0, 0.24)',
    '0px 4px 4px rgba(0, 0, 0, 0.1)', //Index 19: Carousel Controller
    'none',
    'none',
    'none',
    'none',
    'none',
  ],
  components: {
    MuiCssBaseline: {
      html: {
        fontSize: '16px',
      },
      body: {
        fontSize: '16px',
        fontWeight: 'normal',
        lineHeight: 'normal',
      },
    },

    // button
    MuiButtonBase: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontFamily: 'inherit',
          fontSize: 'inherit',
        },
      },
    },

    // input
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },

    // list selection
    MuiMenu: {
      styleOverrides: {
        list: {
          '.MuiMenuItem-root': {
            '&.Mui-focused': {
              backgroundColor: athensGrey,
            },
            '&:hover': {
              backgroundColor: athensGrey,
            },
            '&.Mui-focused[aria-selected="true"]': {
              backgroundColor: cornflowerBlue,
            },
            '&[aria-selected="true"], &[aria-selected="true"]:hover': {
              backgroundColor: cornflowerBlue,
            },
          },
        },
      },
    },

    // default autocomplete style overrides
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          '.MuiAutocomplete-option': {
            '&.Mui-focused': {
              backgroundColor: athensGrey,
            },
            '&:hover': {
              backgroundColor: athensGrey,
            },
            '&.Mui-focused[aria-selected="true"]': {
              backgroundColor: cornflowerBlue,
            },
            '&[aria-selected="true"], &[aria-selected="true"]:hover': {
              backgroundColor: cornflowerBlue,
            },
          },
        },
      },
    },
  },

  /**
   * Default Severity colors
   */
  severityStyles: {
    info: {
      color: blue,
      background: light.blue,
    },
    success: {
      color: green,
      background: light.green,
    },
    warning: {
      color: brown,
      background: light.yellow,
    },
    error: {
      color: red,
      background: light.red,
    },
  } as SeverityStyles,

  /**
   * Default button variant color palette
   */
  buttonVariantStyles,

  /**
   * Default CTA styles
   */
  ctaStyles: {
    button: {
      background: purple,
      borderColor: purple,
      color: white,
      iconStrokeColor: 'transparent',
      iconFillColor: white,
      hover: {
        background: light1.purple,
        borderColor: light1.purple,
        color: white,
        iconFillColor: white,
        iconStrokeColor: 'transparent',
      },
    },
    container: {
      background: light.purple,
      borderColor: 'transparent',
      borderRadius: '',
    },
  } as CTAStyles,

  /**
   * Default darkgray tooltip style
   */
  tooltipStyles: {
    background: grey,
    color: white,
    borderColor: '',
    link: white,
  } as TooltipStyles,

  /**
   * Default selection styles
   */
  listSelectionStyles: {
    backgroundColor: {
      hover: athensGrey,
      focused: cornflowerBlue,
      selected: cornflowerBlue,
    },
  } as ListSelectionStyles,
}

/**
 * Create light theme with optional custom styles
 */
export const createLightTheme = (customStyles?: CustomStyles): Theme => {
  const hasCustomStyles = customStyles && Object.keys(customStyles).length > 0

  const themeOptions = {
    ...themeProps,
    typography: {
      ...themeProps.typography,
      fontFamily: customStyles?.fontFamily || themeProps.typography.fontFamily,

      // override font color
      allVariants: {
        color: customStyles?.fontColor,
      },
    },

    // set text primary color with custom fontColor
    palette: {
      ...themeProps.palette,
      text: {
        ...themeProps.palette.text,
        dark: {
          ...themeProps.palette.text.dark,
          primary: customStyles?.fontColor || themeProps.palette.text.dark.primary,
        },
      },
    },
  }

  return createTheme(
    themeOptions as unknown as ThemeOptions,
    hasCustomStyles
      ? {
          // we only have light theme for buttons
          buttonVariantStyles: {
            fill: {
              primary: {
                light: {
                  ...customStyles?.buttonStyles?.fill?.primary,
                },
              },
              secondary: {
                light: {
                  ...customStyles?.buttonStyles?.fill?.secondary,
                },
              },
            },
            outline: {
              primary: {
                light: {
                  ...customStyles?.buttonStyles?.outline?.primary,
                },
              },
              secondary: {
                light: {
                  ...customStyles?.buttonStyles?.outline?.secondary,
                },
              },
            },
          },
          ctaStyles: { ...customStyles?.ctaStyles },
          severityStyles: { ...customStyles?.severityStyles },
          tooltipStyles: { ...customStyles?.tooltipStyles },

          // apply custom style overrides
          components: {
            // button
            MuiButtonBase: {
              styleOverrides: {
                root: {
                  borderRadius: customStyles?.buttonStyles?.borderRadius,
                },
              },
            },

            // input
            MuiOutlinedInput: {
              styleOverrides: {
                root: {
                  borderRadius: customStyles?.inputStyles?.borderRadius,
                },
              },
            },

            // list
            MuiMenu: {
              styleOverrides: {
                list: {
                  '.MuiMenuItem-root': {
                    '&.Mui-focused': {
                      backgroundColor: customStyles?.listSelectionStyles?.backgroundColor?.focused,
                    },
                    '&:hover': {
                      backgroundColor: customStyles?.listSelectionStyles?.backgroundColor?.hover,
                    },
                    '&.Mui-focused[aria-selected="true"]': {
                      backgroundColor: customStyles?.listSelectionStyles?.backgroundColor?.selected,
                    },
                    '&[aria-selected="true"], &[aria-selected="true"]:hover': {
                      backgroundColor: customStyles?.listSelectionStyles?.backgroundColor?.selected,
                    },
                  },
                },
              },
            },

            // autocomplete
            MuiAutocomplete: {
              styleOverrides: {
                listbox: {
                  '.MuiAutocomplete-option': {
                    '&.Mui-focused': {
                      backgroundColor: customStyles?.listSelectionStyles?.backgroundColor?.focused,
                    },
                    '&:hover': {
                      backgroundColor: customStyles?.listSelectionStyles?.backgroundColor?.hover,
                    },
                    '&.Mui-focused[aria-selected="true"]': {
                      backgroundColor: customStyles?.listSelectionStyles?.backgroundColor?.selected,
                    },
                    '&[aria-selected="true"], &[aria-selected="true"]:hover': {
                      backgroundColor: customStyles?.listSelectionStyles?.backgroundColor?.selected,
                    },
                  },
                },
              },
            },
          },
        }
      : {}
  )
}
